import React from "react";
import { Box, Typography, Stack, Paper, Divider } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmailIcon from "@mui/icons-material/Email";
import { FriendlyButton } from "../shared";

/**
 * DonationSuccess Component
 * Displays confirmation after successful donation
 */
const DonationSuccess = ({
  t,
  donorData,
  amount,
  totalAmount,
  paymentType,
  frequency,
  transactionId,
  promiseId,
  onNewDonation,
  onClose,
  returnToPromises = false,
}) => {
  return (
    <Box sx={{ textAlign: "center", py: 2 }}>
      {/* Success Icon */}
      <Box
        sx={{
          width: 100,
          height: 100,
          borderRadius: "50%",
          backgroundColor: "success.100",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 3,
        }}
      >
        <CheckCircleOutlineIcon
          sx={{ fontSize: 60, color: "success.main" }}
        />
      </Box>

      {/* Success Message */}
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, color: "success.main", mb: 1 }}
      >
        {t("thank_you", "Thank You!")}
      </Typography>
      <Typography
        variant="h6"
        sx={{ fontWeight: 500, color: "text.secondary", mb: 3 }}
      >
        {t("donation_successful", "Your donation was successful")}
      </Typography>

      {/* Receipt Summary */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 2,
          backgroundColor: "grey.50",
          maxWidth: 400,
          mx: "auto",
          mb: 4,
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">
              {t("donor", "Customer")}
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>
              {donorData?.first_name} {donorData?.last_name}
            </Typography>
          </Stack>

          <Divider />

          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">
              {t("donation_type", "Type")}
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>
              {paymentType === "one_time"
                ? t("one_time", "One-Time")
                : t("split_payment", "Split Payment")}
            </Typography>
          </Stack>

          {paymentType === "split" && frequency && (
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">
                {t("frequency", "Frequency")}
              </Typography>
              <Typography sx={{ fontWeight: 500, textTransform: "capitalize" }}>
                {t(frequency, frequency)}
              </Typography>
            </Stack>
          )}

          <Divider />

          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontWeight: 600 }}>
              {t("total_amount", "Total Amount")}
            </Typography>
            <Typography
              sx={{ fontWeight: 700, fontSize: "1.25rem", color: "success.main" }}
            >
              ${Number(totalAmount || amount).toFixed(2)}
            </Typography>
          </Stack>

          {transactionId && (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary" variant="body2">
                  {t("transaction_id", "Transaction ID")}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {transactionId}
                </Typography>
              </Stack>
            </>
          )}

          {promiseId && (
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary" variant="body2">
                {t("promise_id", "Promise ID")}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {promiseId}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Email Confirmation Notice */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: "primary.50",
          borderColor: "primary.200",
          maxWidth: 400,
          mx: "auto",
          mb: 4,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <EmailIcon color="primary" />
          <Typography variant="body2" color="text.secondary" textAlign="left">
            {t(
              "receipt_email_sent",
              "A confirmation receipt has been sent to"
            )}{" "}
            <strong>{donorData?.email}</strong>
          </Typography>
        </Stack>
      </Paper>

      {/* Single next action */}
      {(returnToPromises ? onNewDonation : onClose || onNewDonation) && (
        <FriendlyButton
          onClick={returnToPromises ? onNewDonation : onClose || onNewDonation}
          sx={{ width: "100%", maxWidth: 400, mx: "auto" }}
        >
          {returnToPromises
            ? t("back_to_promises", "Back to Promises")
            : t("close", "Close")}
        </FriendlyButton>
      )}
    </Box>
  );
};

export default DonationSuccess;
